/**
 * All activity_logs between two timestamps, fetched once (not a live
 * subscription — a report is a snapshot; there is a Refresh button). Paged with
 * startAfter so a busy month is read in a few round trips, and capped so a
 * mistaken "all time" range cannot pull the whole collection into the browser.
 * Needs no composite index: single-field range on `ts` + orderBy `ts`.
 */
import {
  getDocs, limit, orderBy, query, startAfter, where,
  type Query, type QueryConstraint, type QueryDocumentSnapshot, type QuerySnapshot,
} from 'firebase/firestore';
import { useCallback, useEffect, useState } from 'react';
import { activityLogsCol, type ActivityLogWithId } from '../lib/collections';

const PAGE_SIZE = 2000;
/** Hard cap per fetch; the page shows a notice when it is hit. */
export const ACTIVITY_RANGE_MAX = 40_000;

export interface ActivityRangeState {
  data: ActivityLogWithId[];
  loading: boolean;
  error: string | null;
  /** True when the cap was reached — counts are then lower bounds. */
  truncated: boolean;
  fetchedAt: number | null;
  reload: () => void;
}

export function useActivityRange(from: number | null, to: number | null): ActivityRangeState {
  const [state, setState] = useState<Omit<ActivityRangeState, 'reload'>>({
    data: [], loading: false, error: null, truncated: false, fetchedAt: null,
  });
  const [nonce, setNonce] = useState(0);
  const reload = useCallback(() => setNonce((n) => n + 1), []);

  useEffect(() => {
    if (from === null || to === null) {
      setState({ data: [], loading: false, error: null, truncated: false, fetchedAt: null });
      return;
    }
    let cancelled = false;
    setState((s) => ({ ...s, loading: true, error: null }));
    (async () => {
      const all: ActivityLogWithId[] = [];
      let cursor: QueryDocumentSnapshot<ActivityLogWithId> | null = null;
      let truncated = false;
      try {
        for (;;) {
          const constraints: QueryConstraint[] = [
            where('ts', '>=', from), where('ts', '<=', to), orderBy('ts', 'desc'), limit(PAGE_SIZE),
          ];
          if (cursor) constraints.push(startAfter(cursor));
          const q: Query<ActivityLogWithId> = query(activityLogsCol, ...constraints);
          const snap: QuerySnapshot<ActivityLogWithId> = await getDocs(q);
          if (cancelled) return;
          for (const d of snap.docs) all.push(d.data());
          if (snap.docs.length < PAGE_SIZE) break;
          if (all.length >= ACTIVITY_RANGE_MAX) { truncated = true; break; }
          cursor = snap.docs[snap.docs.length - 1];
        }
        setState({ data: all, loading: false, error: null, truncated, fetchedAt: Date.now() });
      } catch (err) {
        if (cancelled) return;
        setState({ data: [], loading: false, error: err instanceof Error ? err.message : String(err), truncated: false, fetchedAt: null });
      }
    })();
    return () => { cancelled = true; };
  }, [from, to, nonce]);

  return { ...state, reload };
}
